import {memo, useCallback, useEffect, useRef, useState} from 'react';
import {useWizard} from 'react-use-wizard';
import styled from 'styled-components';
import Papa from 'papaparse';
import {
  InvitedUserFields,
  Palette,
  TranslatedLanguage,
  sanitizeString,
} from 'hubbl-shared';
import {
  PaddingBottom,
  TextHeading1,
  TextHeading6,
  TextHeading7,
  PrimaryButton,
  SecondaryButton,
  Input,
} from '../../../../../../../components';
import {
  useAppDispatch,
  useAppSelector,
  useI18n,
} from '../../../../../../../hooks';
import {GetStartedActions} from '../../../../../../../store/get-started/GetStartedActions';
import {GetStartedSelector} from '../../../../../../../store/get-started/GetStartedSelector';
import {UserSelector} from '../../../../../../../store/user/UserSelector';
import {CompanySelector} from '../../../../../../../store/company/CompanySelector';
import {
  GetStartedWizardStepContainer,
  GetStartedWizardStepContainerProps,
} from '../../step-container/GetStartedWizardStepContainer';
import {
  AssetIconAdd,
  AssetIconTrash,
  AssetIconDownload,
} from '../../../../../../../assets';
import {IconButton} from '../../../../../../../components/buttons/icon-button/IconButton';

const EmailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const REQUIRED_CSV_HEADERS = ['email', 'fullname', 'department', 'jobtitle'];

const DropZone = styled.div<{isDragging: boolean}>`
  border: 2px dashed
    ${props => (props.isDragging ? Palette.primaryCherokee : '#ccc')};
  border-radius: 8px;
  padding: 20px 24px;
  text-align: center;
  background: ${props =>
    props.isDragging ? Palette.primarySubtleGreen : Palette.white};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  margin-top: 24px;
  width: 100%;
`;

const EmployeeRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
`;

const EmployeeList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
`;

const ManualEntryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fee2e2;
  border-radius: 8px;
  margin-top: 16px;
`;

const DownloadButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
`;

const AddButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
`;

const SkipText = styled.button`
  background: none;
  border: none;
  color: ${Palette.almostBlack500};
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 8px;

  &:hover {
    color: ${Palette.almostBlack};
  }
`;

type ManualInviteForm = {
  email: string;
  fullName: string;
  department: string;
  jobTitle: string;
};

const buildDepartmentField = (label: string) => {
  const trimmedLabel = label.trim();
  return {
    id: sanitizeString(trimmedLabel),
    label: {
      [TranslatedLanguage.English]: trimmedLabel,
      [TranslatedLanguage.Danish]: trimmedLabel,
    },
  };
};

const buildInviteFromManualForm = (
  form: ManualInviteForm,
  companyId: string,
  companyName: string,
): InvitedUserFields => ({
  email: form.email.trim().toLowerCase(),
  fullName: form.fullName.trim(),
  jobTitle: form.jobTitle.trim(),
  companyId,
  companyName,
  department: buildDepartmentField(form.department),
});

const normalizeCsvValue = (value?: string | null) =>
  (value ?? '').toString().trim();

export const Step2 = memo(
  ({previousStep}: GetStartedWizardStepContainerProps) => {
    const dispatch = useAppDispatch();
    const {nextStep} = useWizard();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const storedInvitedEmployees = useAppSelector(
      GetStartedSelector.getInvitedEmployees,
    );
    const restrictedMailDomains = useAppSelector(
      GetStartedSelector.getRestrictedMailDomains,
    );
    const [invitedEmployees, setInvitedEmployees] = useState<
      InvitedUserFields[]
    >([]);
    const [newEmail, setNewEmail] = useState('');
    const [newFullName, setNewFullName] = useState('');
    const [newDepartment, setNewDepartment] = useState('');
    const [newJobTitle, setNewJobTitle] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const company = useAppSelector(CompanySelector.getCompanyDetails);
    const t = useI18n();

    const handleContinue = useCallback(() => {
      dispatch(GetStartedActions.setInvitedEmployees(invitedEmployees));
      nextStep();
    }, [invitedEmployees, dispatch, nextStep]);

    const handleSkip = useCallback(() => {
      dispatch(GetStartedActions.setInvitedEmployees([]));
      nextStep();
    }, [dispatch, nextStep]);

    // Load invited employees from Redux on mount
    useEffect(() => {
      if (storedInvitedEmployees.length > 0) {
        setInvitedEmployees(storedInvitedEmployees);
      }
    }, [storedInvitedEmployees]);

    const validateEmailDomain = useCallback(
      (email: string): boolean => {
        if (!restrictedMailDomains?.length) return true;

        const domain = email.split('@')[1];
        return restrictedMailDomains.includes(domain);
      },
      [restrictedMailDomains],
    );

    const parseCsvFile = useCallback(
      async (file: File): Promise<ManualInviteForm[]> => {
        const fileContent = await file.text();

        const withHeader = Papa.parse<Record<string, string>>(fileContent, {
          header: true,
          skipEmptyLines: true,
          transformHeader: header => header.trim().toLowerCase(),
        });

        const headerFields = withHeader.meta.fields ?? [];
        const hasAllRequiredHeaders = REQUIRED_CSV_HEADERS.every(header =>
          headerFields.includes(header),
        );

        if (hasAllRequiredHeaders) {
          return withHeader.data.map(row => ({
            email: normalizeCsvValue(row.email),
            fullName: normalizeCsvValue(row.fullname ?? row.fullName),
            department: normalizeCsvValue(row.department),
            jobTitle: normalizeCsvValue(row.jobtitle ?? row.jobTitle),
          }));
        }

        const withoutHeader = Papa.parse<string[]>(fileContent, {
          header: false,
          skipEmptyLines: true,
        });

        return withoutHeader.data.map(columns => ({
          email: normalizeCsvValue(columns[0]),
          fullName: normalizeCsvValue(columns[1]),
          department: normalizeCsvValue(columns[2]),
          jobTitle: normalizeCsvValue(columns[3]),
        }));
      },
      [],
    );

    const handleFileUpload = useCallback(
      async (file: File) => {
        setLoading(true);
        setError(null);

        try {
          const rows = await parseCsvFile(file);

          if (!rows.length) {
            setError(t.common.noValidEmailsFoundInCSV);
            setLoading(false);
            return;
          }

          const companyName = company?.companyName ?? '';
          const companyId = userDetails?.companyId ?? '';
          const freshInvites: InvitedUserFields[] = [];
          const skippedRows: number[] = [];
          const seenEmails = new Set(
            invitedEmployees.map(invite => invite.email.toLowerCase()),
          );

          rows.forEach((row, index) => {
            const normalizedEmail = row.email.toLowerCase();
            const hasAllFields =
              row.email && row.fullName && row.department && row.jobTitle;

            if (
              !hasAllFields ||
              !EmailRegex.test(normalizedEmail) ||
              !validateEmailDomain(normalizedEmail) ||
              seenEmails.has(normalizedEmail)
            ) {
              skippedRows.push(index + 1);
              return;
            }

            seenEmails.add(normalizedEmail);
            freshInvites.push(
              buildInviteFromManualForm(row, companyId, companyName),
            );
          });

          if (!freshInvites.length) {
            setError(t.common.noValidEmailsFoundInCSV);
            setLoading(false);
            return;
          }

          setInvitedEmployees(prev => [...prev, ...freshInvites]);

          if (skippedRows.length) {
            setError(
              `${skippedRows.length} row${
                skippedRows.length === 1 ? '' : 's'
              } skipped due to invalid data, restricted domain, or duplicate emails.`,
            );
          } else {
            setError(null);
          }
        } catch (err) {
          console.error('Error parsing CSV:', err);
          setError(t.common.errorParsingCSVFile);
        } finally {
          setLoading(false);
        }
      },
      [
        parseCsvFile,
        company,
        userDetails,
        invitedEmployees,
        validateEmailDomain,
        t,
      ],
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file && file.type === 'text/csv') {
          handleFileUpload(file);
        }
      },
      [handleFileUpload],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback(() => {
      setIsDragging(false);
    }, []);

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          handleFileUpload(file);
        }
      },
      [handleFileUpload],
    );

    const handleAddEmployee = useCallback(() => {
      if (!newEmail || !newFullName || !newDepartment || !newJobTitle) {
        setError(t.common.pleaseFillRequiredFields);
        return;
      }

      const normalizedEmail = newEmail.trim().toLowerCase();

      if (!EmailRegex.test(normalizedEmail)) {
        setError(t.common.pleaseEnterValidEmailAddress);
        return;
      }

      if (!validateEmailDomain(normalizedEmail)) {
        setError(
          `${t.common.emailDomainNotAllowed} ${restrictedMailDomains?.join(
            ', ',
          )}`,
        );
        return;
      }

      if (invitedEmployees.some(invite => invite.email === normalizedEmail)) {
        setError(t.common.thisEmailAlreadyInList);
        return;
      }

      const companyName = company?.companyName ?? '';
      const companyId = userDetails?.companyId ?? '';
      const newEmployee = buildInviteFromManualForm(
        {
          email: newEmail,
          fullName: newFullName,
          department: newDepartment,
          jobTitle: newJobTitle,
        },
        companyId,
        companyName,
      );

      setInvitedEmployees(prev => [...prev, newEmployee]);
      setNewEmail('');
      setNewFullName('');
      setNewDepartment('');
      setNewJobTitle('');
      setError(null);
    }, [
      newEmail,
      newFullName,
      newDepartment,
      newJobTitle,
      validateEmailDomain,
      company,
      userDetails,
      invitedEmployees,
      t,
    ]);

    const handleRemoveEmployee = useCallback((index: number) => {
      setInvitedEmployees(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleDownloadExample = useCallback(() => {
      const domain =
        company?.settings?.restrictedMailDomains?.[0] || 'company.com';

      const rows = [
        ['email', 'fullName', 'department', 'jobTitle'],
        [`john.doe@${domain}`, 'John Doe', 'Engineering', 'Software Engineer'],
        [
          `jane.smith@${domain}`,
          'Jane Smith',
          'People Operations',
          'HR Business Partner',
        ],
        [`bob.wilson@${domain}`, 'Bob Wilson', 'Design', 'Product Designer'],
        [
          `sarah.jones@${domain}`,
          'Sarah Jones',
          'Finance',
          'Financial Controller',
        ],
        [`mike.brown@${domain}`, 'Mike Brown', 'Sales', 'Account Executive'],
      ];

      const csvContent = rows.map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], {type: 'text/csv'});
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'example_employees.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, [company]);

    return (
      <GetStartedWizardStepContainer previousStep={previousStep}>
        <div style={{width: '100%', maxWidth: '500px'}}>
          <TextHeading1 center text={t.scenes.getStarted.inviteYourTeam} />

          <PaddingBottom amount={20} />

          {/* CSV Download Button */}
          <DownloadButtonContainer>
            <IconButton
              backgroundColor={Palette.white}
              hoverColor={Palette.almostBlackTransparent10}
              text={t.common.downloadExampleCSV}
              color={Palette.almostBlack}
              onClick={handleDownloadExample}
              icon={<AssetIconDownload color={Palette.almostBlack} size={20} />}
            />
          </DownloadButtonContainer>

          {/* CSV Upload Section */}
          <DropZone
            isDragging={isDragging}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}>
            {loading ? (
              <TextHeading7 text={t.common.processingCSVFile} />
            ) : (
              <>
                <TextHeading7 text={t.common.dragAndDropCSVFileHere} />
              </>
            )}
          </DropZone>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{display: 'none'}}
            onChange={handleFileInputChange}
          />

          <PaddingBottom amount={20} />

          {/* Manual Entry Section */}
          <TextHeading6 text={t.scenes.getStarted.orAddEmployeesManually} />
          <PaddingBottom amount={12} />
          <ManualEntryGrid>
            <Input
              isCompact
              placeholder={t.common.email}
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
            />
            <Input
              isCompact
              placeholder={t.common.fullName}
              value={newFullName}
              onChange={e => setNewFullName(e.target.value)}
            />
            <Input
              isCompact
              placeholder={t.common.department}
              value={newDepartment}
              onChange={e => setNewDepartment(e.target.value)}
            />
            <Input
              isCompact
              placeholder={t.common.jobTitle}
              value={newJobTitle}
              onChange={e => setNewJobTitle(e.target.value)}
            />
          </ManualEntryGrid>
          <AddButtonContainer>
            <IconButton
              icon={<AssetIconAdd color={Palette.almostBlack} size={20} />}
              text={t.common.add}
              color={Palette.almostBlack}
              backgroundColor={Palette.white}
              hoverColor={Palette.almostBlackTransparent10}
              onClick={handleAddEmployee}
              disabled={
                !newEmail ||
                !EmailRegex.test(newEmail) ||
                !newFullName ||
                !newDepartment ||
                !newJobTitle
              }
            />
          </AddButtonContainer>

          {/* Employee List */}
          {invitedEmployees.length > 0 && (
            <>
              <PaddingBottom amount={16} />
              <EmployeeList>
                <TextHeading6
                  text={`${invitedEmployees.length} employee(s) to invite:`}
                />
                <PaddingBottom amount={8} />
                {invitedEmployees.map((employee, index) => (
                  <EmployeeRow key={index}>
                    <div style={{flex: 1}}>
                      <TextHeading7 text={employee.fullName} />
                      <TextHeading7
                        text={`${employee.email} • ${employee.department.label[TranslatedLanguage.English]} • ${employee.jobTitle}`}
                        color={Palette.almostBlack500}
                      />
                    </div>
                    <IconButton
                      icon={
                        <AssetIconTrash color={Palette.systemError} size={20} />
                      }
                      onClick={() => handleRemoveEmployee(index)}
                    />
                  </EmployeeRow>
                ))}
              </EmployeeList>
            </>
          )}

          {error && (
            <>
              <PaddingBottom amount={12} />
              <ErrorMessage>
                <TextHeading7 text={error} color={Palette.systemError} />
              </ErrorMessage>
            </>
          )}

          <ButtonContainer>
            <PrimaryButton
              label={`${t.common.continue}${invitedEmployees.length > 0 ? ` (${invitedEmployees.length})` : ''}`}
              onClick={handleContinue}
              isDisabled={loading}
              fullWidth
            />
            <SkipText onClick={handleSkip} disabled={loading}>
              {t.scenes.onboarding.skipForNow}
            </SkipText>
          </ButtonContainer>
        </div>
      </GetStartedWizardStepContainer>
    );
  },
);
